import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Navigation from "./Navigation";

const API_ORIGIN =
  (typeof window !== "undefined" && window.__API_ORIGIN__) ||
  process.env.REACT_APP_API_ORIGIN ||
  "https://app.zoompay.co.ke";
const API_V1 = `${API_ORIGIN}/api/v2`;

function getToken() {
  return (
    localStorage.getItem("zp_token") ||
    localStorage.getItem("token") ||
    localStorage.getItem("accessToken")
  );
}

export default function UnlockCredit() {
  const navigate = useNavigate();
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleAssess = async () => {
    if (!consent) {
      setError("Please accept the financial-data disclosure to continue.");
      return;
    }
    setError(null);
    setLoading(true);
    setResult(null);
    try {
      const res = await fetch(`${API_V1}/snpl/assess`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({ disclosureAccepted: true }),
      });
      const d = await res.json();
      if (!d.ok) {
        setError(d.error || "Assessment failed. Please try again.");
      } else {
        setResult(d);
      }
    } catch (e) {
      setError("Network error: " + e.message);
    }
    setLoading(false);
  };

  return (
    <>
      <Navigation />
      <div className="p-6 max-w-xl mx-auto space-y-6">
        <h1 className="text-2xl font-bold">Unlock Your SNPL Credit</h1>

        {/* Beta notice — honest about the current assessment basis */}
        <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-800">
          Beta: affordability is currently assessed using sample data while our
          bank-connection provider is being finalized. Your granted limit is for
          testing the experience; live bank-verified assessment is coming soon.
        </div>

        {!result && (
          <div className="border p-4 rounded shadow space-y-4">
            <p className="text-sm text-gray-700">
              We assess your affordability to give you a personalized,
              responsible SNPL starting limit &mdash; up to CAD $250. We look at
              income stability, balances, and account history. This is an
              affordability assessment, not a loan application.
            </p>

            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="mt-1"
              />
              <span>
                I consent to ZoomPay assessing my financial data to determine my
                SNPL affordability limit.
              </span>
            </label>

            {error && <p className="text-sm text-red-600">{error}</p>}

            <button
              disabled={loading || !consent}
              onClick={handleAssess}
              className="bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white font-semibold p-2 w-full rounded transition-colors"
            >
              {loading ? "Assessing your affordability\u2026" : "Assess My Affordability"}
            </button>
          </div>
        )}

        {result && result.status === "APPROVED" && (
          <div className="border border-green-300 bg-green-50 p-5 rounded shadow text-center space-y-2">
            <p className="text-sm text-green-700 font-semibold">Credit Unlocked</p>
            <p className="text-3xl font-bold text-green-800">
              CAD ${Number(result.startingLimit).toFixed(2)}
            </p>
            <p className="text-xs text-gray-600">Risk tier: {result.riskTier}</p>
            <button
              onClick={() => navigate("/dashboard")}
              className="mt-3 bg-green-600 hover:bg-green-700 text-white font-semibold p-2 w-full rounded transition-colors"
            >
              Go to Dashboard
            </button>
          </div>
        )}

        {result && result.status !== "APPROVED" && (
          <div className="border border-amber-300 bg-amber-50 p-5 rounded shadow space-y-2">
            <p className="text-sm text-amber-800 font-semibold">Under Review</p>
            <p className="text-sm text-gray-700">
              We&rsquo;re reviewing your affordability assessment. We&rsquo;ll
              notify you once it&rsquo;s complete.
            </p>
            {Array.isArray(result.reasons) && result.reasons.length > 0 && (
              <ul className="text-xs text-gray-500 list-disc pl-5">
                {result.reasons.map((r, i) => (
                  <li key={i}>{r}</li>
                ))}
              </ul>
            )}
            <button
              onClick={() => navigate("/dashboard")}
              className="mt-2 bg-gray-600 hover:bg-gray-700 text-white font-semibold p-2 w-full rounded transition-colors"
            >
              Back to Dashboard
            </button>
          </div>
        )}
      </div>
    </>
  );
}
